import { Link } from '@trashlab/sitesapp/src/components/shared/link';
import { useTenantContext } from '@trashlab/sitesapp/src/contexts/use-tenant-context';
import { Suspense } from 'react';
import ZoneMapWithGeoAreas from '@trashlab/viteapp/src/components/settings/zone-map-with-geoareas';
import { trpcPricingListPriceZonesWithGeoAreasOpen } from '@trashlab/api/src/generated/hooks';
import { PageSEO } from '@trashlab/sitesapp/src/templates/redebins/components/shared/page-seo';

const MapPlaceholder = () => {
  return (
    <div className="w-full h-[600px] bg-white rounded-xl shadow-sm flex items-center justify-center">
      <div className="text-gray-400">Loading service area map...</div>
    </div>
  );
};

// Component to conditionally show map only if zones exist
const ServiceAreasMap = ({ tenantId }: { tenantId: string }) => {
  const { data: zones, isLoading } = trpcPricingListPriceZonesWithGeoAreasOpen.useQuery({
    tenantId,
  });

  // Only show map if zones exist
  if (isLoading) {
    return <MapPlaceholder />;
  }

  if (!zones || zones.length === 0) {
    return null; // Don't show map if no zones configured
  }

  return (
    <div className="mt-12">
      <Suspense fallback={<MapPlaceholder />}>
        <div className="w-full h-[600px] bg-white rounded-xl shadow-sm overflow-hidden">
          <ZoneMapWithGeoAreas
            isPublic={true}
            tenantId={tenantId}
          />
        </div>
      </Suspense>
    </div>
  );
};

export const ServiceAreasContent = () => {
  const { website } = useTenantContext();

  return (
    <>
      <PageSEO pathname="/service-areas" />
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-12 italic text-center">
              Searching for a Dumpster Rental Near Me?
            </h2>

          <div className="grid md:grid-cols-2 gap-12 mb-12">
            {/* Left Column - Content */}
            <div className="space-y-4 text-[#333333]">
              <p>
                Looking for a <strong>dumpster rental near you</strong> in the Twin Cities metro area? <strong>RED-E-BINS</strong> provides affordable, driveway-safe dumpsters for residential and commercial projects, including home cleanouts, renovations, and construction jobs. Our compact, easy-to-use bins make junk removal simple, with <strong>hassle-free delivery and pickup</strong>. We proudly serve the <strong>7-county metro area</strong>, including <strong>Anoka, Carver, Dakota, Hennepin, Ramsey, Scott, and Washington counties</strong>, covering major cities like{' '}
                <Link href="/service-areas/dumpster-rental-minneapolis-mn" className="text-[#E31837] hover:underline">
                  Minneapolis
                </Link>
                ,{' '}
                <Link href="/service-areas/dumpster-rental-st-paul-mn" className="text-[#E31837] hover:underline">
                  Saint Paul
                </Link>
                , Bloomington, Maple Grove, Eagan, Burnsville, Woodbury, and more.
              </p>
              <p>
                Whether you need a <strong>small dumpster rental near me</strong> for a quick decluttering project or <strong>multiple bins for a large-scale job</strong>, we offer <strong>flexible rental options</strong> to fit your needs. Our dumpsters are <strong>driveway-friendly</strong>, ensuring no damage to your property, and we prioritize <strong>eco-friendly disposal</strong>, recycling whenever possible. If you're searching for a <strong>reliable dumpster rental near me</strong>, we make it easy with <strong>transparent pricing, fast delivery, and top-rated customer service</strong>. Call us today or <strong>book online</strong> for a <strong>dumpster rental near you</strong> in <strong>Minneapolis, Saint Paul, and the surrounding suburbs</strong>!
              </p>
            </div>

            {/* Right Column - Image */}
            <div>
              <img
                src="/redebins/images/redareas.jpg"
                alt="RED-E-BINS service areas map showing the Twin Cities 7-county metro area"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>

            {/* Map Component - Only show if zones exist */}
            {website?.organization?.id && (
              <ServiceAreasMap tenantId={website.organization.id} />
            )}
          </div>
        </div>
      </section>
    </>
  );
};

